import React, {useEffect, useId, useRef, useState} from 'react';
import PropTypes from 'prop-types';

import {
  FEATURED_ITEM_DEFAULT_REASON,
  FEATURED_ITEM_HEADING,
  FEATURED_ITEM_TOOLTIP_INTRO,
  FEATURED_ITEM_TOOLTIP_LABEL,
  MESSAGE_TIMEOUT,
} from '../../constants/constants';
import {
  FeaturedHeader,
  FeaturedInfoButton,
  FeaturedInfoWrapper,
  FeaturedLabel,
  FeaturedToolTip,
  FeaturedToolTipReason,
  FeaturedWrapper,
} from './FeaturedProduct.style';

/**
 * Wraps a peer-gap trial card in the search grid with the purple strip that explains why the
 * product is there.
 *
 * It wraps rather than modifies — the child is an ordinary <Product>, rendered untouched, and the
 * strip stands in place of the offer strip the card would otherwise show (see
 * FeaturedProduct.style.js). Nothing in the shared product components knows about the trial, so
 * pulling it out is deleting these two files, the FEATURED_ITEM_* constants and the branch in
 * SearchPage.js.
 */
const FeaturedProduct = ({reason, title, intro, children}) => {
  const [showToolTip, setShowToolTip] = useState(false);
  const timeout = useRef(null);
  // Several featured cards can share a page, so the tooltip needs an id of its own to be
  // described by. useId is stable across the server render and the client hydration.
  const toolTipId = useId();

  useEffect(() => () => clearTimeout(timeout.current), []);

  const openToolTip = () => {
    clearTimeout(timeout.current);
    setShowToolTip(true);
  };
  const closeToolTip = () => {
    clearTimeout(timeout.current);
    setShowToolTip(false);
  };
  // Escape dismisses it, as the tooltip pattern expects. Only reachable while the button holds
  // focus, which is the case that matters: a hover tooltip is dismissed by moving away.
  const onKeyDown = event => {
    if (event.key === 'Escape') {
      closeToolTip();
    }
  };
  // Touch devices never fire hover, so a tap opens the tooltip and it closes itself — the same
  // behaviour as the "Popular" and "Previously purchased" icons on the card below.
  const toggleToolTip = () => {
    if (showToolTip) {
      closeToolTip();
      return;
    }
    setShowToolTip(true);
    timeout.current = setTimeout(() => setShowToolTip(false), MESSAGE_TIMEOUT);
  };

  return (
    <FeaturedWrapper data-rw="featured-product">
      <FeaturedHeader>
        {/* Set per campaign by graph-product-search; the peer-gap trial's wording when unset. */}
        <FeaturedLabel>{title || FEATURED_ITEM_HEADING}</FeaturedLabel>
        <FeaturedInfoWrapper
          onMouseOver={openToolTip}
          onMouseLeave={closeToolTip}
        >
          <FeaturedToolTip
            $show={showToolTip}
            id={toolTipId}
            role="tooltip"
            data-rw="featured-reason"
          >
            {/* Per campaign too: the peer-gap line is false on a merchandising card. */}
            {intro || FEATURED_ITEM_TOOLTIP_INTRO}
            <FeaturedToolTipReason>
              {reason || FEATURED_ITEM_DEFAULT_REASON}
            </FeaturedToolTipReason>
          </FeaturedToolTip>
          <FeaturedInfoButton
            type="button"
            aria-label={FEATURED_ITEM_TOOLTIP_LABEL}
            aria-describedby={toolTipId}
            onClick={toggleToolTip}
            onFocus={openToolTip}
            onBlur={closeToolTip}
            onKeyDown={onKeyDown}
            data-rw="featured-info"
          >
            i
          </FeaturedInfoButton>
        </FeaturedInfoWrapper>
      </FeaturedHeader>
      {children}
    </FeaturedWrapper>
  );
};

FeaturedProduct.propTypes = {
  reason: PropTypes.string,
  title: PropTypes.string,
  intro: PropTypes.string,
  children: PropTypes.node.isRequired,
};

export default FeaturedProduct;
